import React, { Fragment, useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import Spinner from "react-bootstrap/Spinner";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import SendPromoModal from "../components/PromoCode/SendPromoModal.js";

import {
  createPromoCode,
  createPromoCodeRule,
  deletePromoCode,
  getPromoCodes,
  updatePromoCodeRule,
  updatePromoCode,
  togglePromoCodeStatus,
} from "../../store/actions/PromoCodeActions";

const createDefaultRules = () => [
  { service_type: "JOB", billed_type: "monthly", is_active: true },
  { service_type: "JOB", billed_type: "yearly", is_active: true },
  { service_type: "DISK", billed_type: "monthly", is_active: true },
  { service_type: "DISK", billed_type: "yearly", is_active: true },
  { service_type: "PREMIUMSUB", billed_type: "monthly", is_active: true },
  { service_type: "PREMIUMSUB", billed_type: "yearly", is_active: true },
];

const formatDisplayDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const formatToISO = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return !Number.isNaN(d.getTime()) ? d.toISOString() : val;
};

const getDateTimeLocalWithDaysOffset = (daysOffset = 0) => {
  const now = new Date();
  if (daysOffset) {
    now.setDate(now.getDate() + daysOffset);
  }
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
};

const actionIcon = (
  <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <rect x="0" y="0" width="24" height="24"></rect>
      <circle fill="#000000" cx="5" cy="12" r="2"></circle>
      <circle fill="#000000" cx="12" cy="12" r="2"></circle>
      <circle fill="#000000" cx="19" cy="12" r="2"></circle>
    </g>
  </svg>
);

const PromoCodesList = () => {
  const { changeBackground } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const [promoCodes, setPromoCodes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("JOB");
  const [isLoading, setIsLoading] = useState(false);

  const columns = [
    "SNO",
    "Code",
    "Name",
    "Discount Type",
    "Discount Value",
    "Max Redemptions",
    "Per User",
    "Active Status",
    // "Created On",
    // "Updated On",
    "Action",
  ];
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showUpdateRuleModal, setShowUpdateRuleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpdatePromoModal, setShowUpdatePromoModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [selectedDeletePromo, setSelectedDeletePromo] = useState(null);
  const [selectedEditPromo, setSelectedEditPromo] = useState(null);
  const [selectedInfoPromo, setSelectedInfoPromo] = useState(null);
  const [updatingPromoIds, setUpdatingPromoIds] = useState([]);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    discount_type: "PERCENTAGE",
    discount_value: "",
    max_redemptions: "",
    max_redemptions_per_user: "",
    start_at: getDateTimeLocalWithDaysOffset(0),
    expires_at: getDateTimeLocalWithDaysOffset(1),
    is_active: true,
    rules: createDefaultRules(),
  });
  const [ruleData, setRuleData] = useState({
    service_type: "JOB",
    billed_type: "monthly",
    is_active: true,
  });
  const [updateRulesData, setUpdateRulesData] = useState([]);
  const [editFormData, setEditFormData] = useState({
    name: "",
    discount_value: "",
    max_redemptions: "",
    is_active: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isRuleSubmitting, setIsRuleSubmitting] = useState(false);
  const [updatingRuleIds, setUpdatingRuleIds] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedSendPromo, setSelectedSendPromo] = useState(null);

  const handleSendOpen = (promo) => {
    setSelectedSendPromo(promo);
    setShowSendModal(true);
  };

  const handleSendClose = () => {
    setShowSendModal(false);
    setSelectedSendPromo(null);
  };

  const serviceTypes = ["JOB", "DISK", "PREMIUMSUB"];
  const billedTypes = ["monthly", "yearly"];

  const normalizePromoCodes = (data) => {
    if (Array.isArray(data)) {
      return data;
    }

    return data?.results || data?.data || [];
  };

  const fetchPromoCodes = async () => {
    setIsLoading(true);
    try {
      const res = await dispatch(getPromoCodes());
      const list = normalizePromoCodes(res?.payload);
      setPromoCodes(list);
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while loading promo codes!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    fetchPromoCodes();
  }, []);

  const filteredPromoCodes = promoCodes.filter((promo) => {
    // const hasActiveRule = promo?.rules?.some(
    //   (rule) => rule.service_type === activeTab && rule.is_active === true
    // );
    // if (!hasActiveRule) return false;
    if (activeTab === "NONE") {
      const hasTargetActiveRule = promo?.rules?.some(
        (rule) =>
          ["JOB", "DISK", "PREMIUMSUB"].includes(rule.service_type) &&
          rule.is_active === true
      );
      if (hasTargetActiveRule) return false;
    } else {
      const hasActiveRule = promo?.rules?.some(
        (rule) => rule.service_type === activeTab && rule.is_active === true
      );
      if (!hasActiveRule) return false;
    }

    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      promo.code?.toLowerCase().includes(q) ||
      promo.name?.toLowerCase().includes(q) ||
      promo.discount_type?.toLowerCase().includes(q)
    );
  });

  const handleClose = () => {
    setShowAddModal(false);
  };

  const handleOpen = () => {
    setShowAddModal(true);
  };

  const handleInfoOpen = (promo) => {
    setSelectedInfoPromo(promo);
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
    setSelectedInfoPromo(null);
  };

  const handleEditOpen = (promo) => {
    setSelectedEditPromo(promo);
    setEditFormData({
      name: promo?.name || "",
      discount_value: promo?.discount_value ?? "",
      max_redemptions: promo?.max_redemptions ?? "",
      is_active: promo?.is_active ?? true,
    });
    setShowUpdatePromoModal(true);
  };

  const handleEditClose = () => {
    setShowUpdatePromoModal(false);
    setSelectedEditPromo(null);
    setEditFormData({
      name: "",
      discount_value: "",
      max_redemptions: "",
      is_active: true,
    });
  };

  const handleDeleteOpen = (promo) => {
    setSelectedDeletePromo(promo);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedDeletePromo(null);
  };

  const handleRuleOpen = (promo) => {
    setSelectedPromo(promo);
    setRuleData({
      service_type: "JOB",
      billed_type: "monthly",
      is_active: true,
    });
    setShowRuleModal(true);
  };

  const handleUpdateRuleOpen = (promo) => {
    setSelectedPromo(promo);
    setUpdateRulesData(
      (promo?.rules || []).map((rule) => ({
        ...rule,
        is_active: rule.is_active ?? true,
      }))
    );
    setShowUpdateRuleModal(true);
  };

  const handleRuleClose = () => {
    setShowRuleModal(false);
    setSelectedPromo(null);
  };

  const handleUpdateRuleClose = () => {
    setShowUpdateRuleModal(false);
    setSelectedPromo(null);
    setUpdateRulesData([]);
  };

  const handleDeletePromo = async () => {
    if (!selectedDeletePromo?.id) return;

    try {
      const res = await dispatch(
        deletePromoCode({ promo_id: selectedDeletePromo.id })
      );

      if (res?.payload?.success) {
        toast.success("Promo code deleted successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });
        handleDeleteClose();
        fetchPromoCodes();
      } else {
        throw new Error(
          res?.payload?.message || "Something went wrong while deleting promo code!"
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while deleting promo code!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    }
  };

  const handlePromoStatusToggle = async (promoId) => {
    const currentPromo = promoCodes.find((promo) => promo.id === promoId);
    if (!currentPromo) return;

    const previousActive = !!currentPromo.is_active;
    const nextActive = !previousActive;

    setUpdatingPromoIds((prev) => [...prev, promoId]);
    setPromoCodes((prev) =>
      prev.map((promo) =>
        promo.id === promoId ? { ...promo, is_active: nextActive } : promo
      )
    );

    try {
      const res = await dispatch(
        togglePromoCodeStatus({
          promo_id: promoId,
          body: { is_active: nextActive },
        })
      );

      if (res?.payload?.success || typeof res?.payload?.is_active === "boolean") {
        toast.success(res?.payload?.message || "Promo status updated.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });
      } else {
        throw new Error(
          res?.payload?.message || "Something went wrong while updating promo status!"
        );
      }
    } catch (error) {
      setPromoCodes((prev) =>
        prev.map((promo) =>
          promo.id === promoId ? { ...promo, is_active: previousActive } : promo
        )
      );
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while updating promo status!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setUpdatingPromoIds((prev) => prev.filter((id) => id !== promoId));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditToggleChange = (e) => {
    const { name, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleRuleChange = (serviceType, billedType) => {
    setFormData((prev) => ({
      ...prev,
      rules: prev.rules.map((rule) =>
        rule.service_type === serviceType && rule.billed_type === billedType
          ? { ...rule, is_active: !rule.is_active }
          : rule
      ),
    }));
  };

  const handleRuleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRuleData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUpdateRuleToggle = async (ruleId) => {
    if (!selectedPromo?.id) return;

    const targetRule = updateRulesData.find((rule) => rule.id === ruleId);
    if (!targetRule) return;

    const nextActive = !targetRule.is_active;
    const previousActive = targetRule.is_active;

    setUpdatingRuleIds((prev) => [...prev, ruleId]);
    setUpdateRulesData((prev) =>
      prev.map((rule) =>
        rule.id === ruleId ? { ...rule, is_active: nextActive } : rule
      )
    );

    try {
      const res = await dispatch(
        updatePromoCodeRule({
          promo_id: selectedPromo.id,
          rule_id: ruleId,
          body: {
            is_active: nextActive,
          },
        })
      );

      if (res?.payload?.success || res?.payload?.rule?.id) {
        toast.success("Rule updated successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });
        // fetchPromoCodes();
      } else {
        throw new Error("Something went wrong while updating rule!");
      }
    } catch (error) {
      setUpdateRulesData((prev) =>
        prev.map((rule) =>
          rule.id === ruleId ? { ...rule, is_active: previousActive } : rule
        )
      );
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while updating rule!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setUpdatingRuleIds((prev) => prev.filter((id) => id !== ruleId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      code: formData.code.trim(),
      name: formData.name.trim(),
      discount_type: formData.discount_type,
      discount_value: formData.discount_value,
      max_redemptions: Number(formData.max_redemptions),
      max_redemptions_per_user: Number(formData.max_redemptions_per_user),
      ...(formData.start_at && { start_at: formatToISO(formData.start_at) }),
      ...(formData.expires_at && { expires_at: formatToISO(formData.expires_at) }),
      is_active: formData.is_active,
      rules: formData.rules,
    };

    try {
      const res = await dispatch(createPromoCode(payload));

      if (res?.payload?.id) {
        toast.success("Promo code created successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });

        setShowAddModal(false);
        setFormData({
          code: "",
          name: "",
          discount_type: "PERCENTAGE",
          discount_value: "",
          max_redemptions: "",
          max_redemptions_per_user: "",
          start_at: getDateTimeLocalWithDaysOffset(0),
          expires_at: getDateTimeLocalWithDaysOffset(1),
          is_active: true,
          rules: createDefaultRules(),
        });
        fetchPromoCodes();
      } else {
        toast.error(
          res?.payload?.message ||
            "Something went wrong while creating promo code!",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
          }
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while creating promo code!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEditPromo?.id) return;

    setIsEditSubmitting(true);

    const payload = {
      name: editFormData.name.trim(),
      discount_value: editFormData.discount_value,
      max_redemptions: Number(editFormData.max_redemptions),
      is_active: editFormData.is_active,
    };

    try {
      const res = await dispatch(
        updatePromoCode({
          promo_id: selectedEditPromo.id,
          body: payload,
        })
      );

      if (res?.payload?.id) {
        toast.success("Promo code updated successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });
        handleEditClose();
        fetchPromoCodes();
      } else {
        throw new Error(
          res?.payload?.message || "Something went wrong while updating promo!"
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while updating promo!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleRuleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPromo?.id) return;

    setIsRuleSubmitting(true);

    try {
      const res = await dispatch(
        createPromoCodeRule({
          promo_id: selectedPromo.id,
          body: ruleData,
        })
      );

      if (res?.payload?.service_type) {
        toast.success("Rule added successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        });

        setShowRuleModal(false);
        setSelectedPromo(null);
        setRuleData({
          service_type: "JOB",
          billed_type: "monthly",
          is_active: true,
        });
        fetchPromoCodes();
      } else {
        toast.error(
          res?.payload?.message || "Something went wrong while adding rule!",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
          }
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.data?.detail ||
          error?.message ||
          "Something went wrong while adding rule!",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    } finally {
      setIsRuleSubmitting(false);
    }
  };

  return (
    <Fragment>
      <div className="col-12">
        <div className="card">
          <div className="card-header border-bottom pb-3 pt-4 px-4">
            <ul
              className="nav nav-pills d-flex align-items-center"
              style={{ gap: "12px", borderBottom: "none" }}
              role="tablist"
            >
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${
                    activeTab === "JOB"
                      ? "active font-w600 text-white"
                      : "text-dark font-w500 bg-light"
                  }`}
                  onClick={() => setActiveTab("JOB")}
                  style={{
                    fontSize: "15px",
                    padding: "8px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Jobs
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${
                    activeTab === "DISK"
                      ? "active font-w600 text-white"
                      : "text-dark font-w500 bg-light"
                  }`}
                  onClick={() => setActiveTab("DISK")}
                  style={{
                    fontSize: "15px",
                    padding: "8px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Disk
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${
                    activeTab === "PREMIUMSUB"
                      ? "active font-w600 text-white"
                      : "text-dark font-w500 bg-light"
                  }`}
                  onClick={() => setActiveTab("PREMIUMSUB")}
                  style={{
                    fontSize: "15px",
                    padding: "8px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  Premium
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${
                    activeTab === "NONE"
                      ? "active font-w600 text-white"
                      : "text-dark font-w500 bg-light"
                  }`}
                  onClick={() => setActiveTab("NONE")}
                  style={{
                    fontSize: "15px",
                    padding: "8px 24px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  None
                </button>
              </li>
            </ul>
          </div>
          <div className="card-body">
            <div className="w-100 table-responsive">
              <div id="example_wrapper" className="dataTables_wrapper">
                <div id="example_filter" className="dataTables_filter">
                  <label>
                    Search:
                    <input
                      type="search"
                      className=""
                      placeholder=""
                      aria-controls="example"
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </label>
                </div>
                {isLoading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : (
                  <table id="example3" className="display w-100 dataTable">
                    <thead>
                      <tr role="row">
                        {columns.map((d, i) => (
                          <th key={i}>{d}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPromoCodes?.length ? (
                        filteredPromoCodes.map((promo, i) => (
                          <tr key={promo.id}>
                            <td style={{fontSize:'14px'}}>{i + 1}</td>
                            <td style={{ fontWeight: 600,fontSize:'14px' }}>{promo.code}</td>
                            <td style={{fontSize:'14px'}}>{promo.name}</td>
                            <td style={{fontSize:'14px'}}>{promo.discount_type}</td>
                            <td style={{fontSize:'14px'}}>{promo.discount_value}</td>
                            <td style={{fontSize:'14px'}}>{promo.max_redemptions}</td>
                            <td style={{fontSize:'14px'}}>{promo.max_redemptions_per_user}</td>
                            <td className="text-center">
                              <div
                                className="d-inline-flex align-items-center gap-2"
                                style={{
                                  minWidth: "140px",
                                  width: "100%",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => handlePromoStatusToggle(promo.id)}
                                  disabled={updatingPromoIds.includes(promo.id)}
                                  aria-pressed={promo.is_active}
                                  title={
                                    promo.is_active
                                      ? "Deactivate promo"
                                      : "Activate promo"
                                  }
                                  style={{
                                    width: "48px",
                                    height: "26px",
                                    borderRadius: "999px",
                                    border: "none",
                                    padding: "3px",
                                    backgroundColor: promo.is_active
                                      ? "#2f6b1f"
                                      : "#ef3d32",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: promo.is_active
                                      ? "flex-end"
                                      : "flex-start",
                                    cursor: updatingPromoIds.includes(promo.id)
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: updatingPromoIds.includes(promo.id)
                                      ? 0.7
                                      : 1,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      backgroundColor: "#ffffff",
                                      display: "block",
                                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
                                    }}
                                  />
                                </button>
                                <span
                                  className="small"
                                  style={{
                                    minWidth: "64px",
                                    textAlign: "left",
                                    whiteSpace: "nowrap",
                                    color: promo?.is_active?'#2f6b1f':'#ef3d32'
                                  }}
                                >
                                  {promo.is_active ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </td>
                            {/* <td style={{fontSize:'14px'}}>{formatDisplayDate(promo.created_at)}</td>
                            <td style={{fontSize:'14px'}}>{formatDisplayDate(promo.updated_at)}</td> */}
                            <td>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant={
                                    promo.is_active ? "success" : "danger"
                                  }
                                  className="light sharp i-false"
                                >
                                  {actionIcon}
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                  renderOnMount
                                  style={{ zIndex: 9999 }}
                                >
                                  <Dropdown.Item onClick={() => handleInfoOpen(promo)}>
                                    Info
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleSendOpen(promo)}>
                                    Send Code
                                  </Dropdown.Item>
                                  <Link to={`/promo-users?promo_id=${promo.id}`} className="dropdown-item">
                                    View Promo Users
                                  </Link>
                                  <Dropdown.Item onClick={() => handleEditOpen(promo)}>
                                    Edit Promo
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleRuleOpen(promo)}>
                                    Add Rule
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => handleUpdateRuleOpen(promo)}
                                    disabled={!promo?.rules?.length}
                                  >
                                    Update Rules
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    onClick={() => handleDeleteOpen(promo)}
                                    className="text-danger"
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columns.length} className="text-center py-4">
                            No promo codes found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr role="row">
                        {columns.map((d, i) => (
                          <th key={i}>{d}</th>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                )}
                <div className="d-sm-flex text-center justify-content-between align-items-center mt-3 dataTables_info_users">
                  <div className="dataTables_info">
                    Showing {filteredPromoCodes?.length ? 1 : 0} to{" "}
                    {filteredPromoCodes?.length || 0} of {promoCodes?.length || 0}{" "}
                    entries
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ maxWidth: "900px" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Promo Code</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClose}
                />
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Code</label>
                      <input
                        type="text"
                        name="code"
                        className="form-control"
                        placeholder="Enter code"
                        value={formData.code}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder="Enter promo name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Discount Type</label>
                      <select
                        name="discount_type"
                        className="form-control"
                        value={formData.discount_type}
                        onChange={handleChange}
                      >
                        <option value="PERCENTAGE">PERCENTAGE</option>
                        <option value="FIXED">FIXED</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Discount Value</label>
                      <input
                        type="text"
                        name="discount_value"
                        className="form-control"
                        placeholder="e.g. 10.00"
                        value={formData.discount_value}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max Redemptions</label>
                      <input
                        type="number"
                        min="0"
                        name="max_redemptions"
                        className="form-control"
                        placeholder="e.g. 100"
                        value={formData.max_redemptions}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max Redemptions Per User</label>
                      <input
                        type="number"
                        min="0"
                        name="max_redemptions_per_user"
                        className="form-control"
                        placeholder="e.g. 1"
                        value={formData.max_redemptions_per_user}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="is_active"
                          className="form-check-input"
                          id="promoIsActive"
                          checked={formData.is_active}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="promoIsActive">
                          Active
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label mb-2">Rules</label>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Service Type</th>
                              {billedTypes.map((billedType) => (
                                <th key={billedType} className="text-center">
                                  {billedType}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {serviceTypes.map((serviceType) => (
                              <tr key={serviceType}>
                                <td>{serviceType}</td>
                                {billedTypes.map((billedType) => {
                                  const rule = formData.rules.find(
                                    (item) =>
                                      item.service_type === serviceType &&
                                      item.billed_type === billedType
                                  );

                                  return (
                                    <td key={`${serviceType}-${billedType}`} className="text-center">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                        checked={rule?.is_active ?? false}
                                        onChange={() =>
                                          handleRuleChange(serviceType, billedType)
                                        }
                                      />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleClose}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : "Save Promo Code"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showUpdatePromoModal && selectedEditPromo && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "560px" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Promo</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleEditClose}
                />
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        value={editFormData.name}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Discount Value</label>
                      <input
                        type="text"
                        name="discount_value"
                        className="form-control"
                        value={editFormData.discount_value}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Max Redemptions</label>
                      <input
                        type="number"
                        min="0"
                        name="max_redemptions"
                        className="form-control"
                        value={editFormData.max_redemptions}
                        onChange={handleEditChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          name="is_active"
                          className="form-check-input"
                          id="promoEditIsActive"
                          checked={editFormData.is_active}
                          onChange={handleEditToggleChange}
                          style={{ cursor: "pointer" }}
                        />
                        <label
                          className="form-check-label mb-0"
                          htmlFor="promoEditIsActive"
                        >
                          {editFormData.is_active ? "Active" : "Inactive"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleEditClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isEditSubmitting}
                  >
                    {isEditSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && selectedDeletePromo && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "460px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">Delete Promo Code</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleDeleteClose}
                />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Are you sure you want to delete promo code{" "}
                  <strong>{selectedDeletePromo.code}</strong>?
                </p>
                <p className="text-muted mb-0">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light px-4 py-2 rounded-pill" onClick={handleDeleteClose}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger rounded-pill px-4 py-2" onClick={handleDeletePromo}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRuleModal && selectedPromo && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "560px" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add Rule for {selectedPromo.code}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleRuleClose}
                />
              </div>
              <form onSubmit={handleRuleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Service Type</label>
                      <select
                        name="service_type"
                        className="form-control"
                        value={ruleData.service_type}
                        onChange={handleRuleFormChange}
                      >
                        <option value="JOB">JOB</option>
                        <option value="DISK">DISK</option>
                        <option value="PREMIUMSUB">PREMIUMSUB</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Billed Type</label>
                      <select
                        name="billed_type"
                        className="form-control"
                        value={ruleData.billed_type}
                        onChange={handleRuleFormChange}
                      >
                        <option value="monthly">monthly</option>
                        <option value="yearly">yearly</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          name="is_active"
                          className="form-check-input"
                          id="ruleIsActive"
                          checked={ruleData.is_active}
                          onChange={handleRuleFormChange}
                        />
                        <label className="form-check-label" htmlFor="ruleIsActive">
                          Active
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light"
                    onClick={handleRuleClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isRuleSubmitting}
                  >
                    {isRuleSubmitting ? "Saving..." : "Save Rule"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showUpdateRuleModal && selectedPromo && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: "600px" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Update Rules for <span className="text-primary font-w600">{selectedPromo.code}</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleUpdateRuleClose}
                />
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Service Type</th>
                        <th>Billed Type</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {updateRulesData.length ? (
                        updateRulesData.map((rule) => (
                          <tr key={rule.id}>
                            <td className="font-w600 text-dark">{rule.service_type}</td>
                            <td className="text-capitalize">{rule.billed_type}</td>
                            <td className="text-center">
                              <div
                                className="d-inline-flex justify-content-center align-items-center gap-2"
                                style={{
                                  minWidth: "130px",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleUpdateRuleToggle(rule.id)
                                  }
                                  disabled={updatingRuleIds.includes(rule.id)}
                                  aria-pressed={rule.is_active}
                                  title={
                                    rule.is_active
                                      ? "Deactivate rule"
                                      : "Activate rule"
                                  }
                                  style={{
                                    width: "48px",
                                    height: "26px",
                                    borderRadius: "999px",
                                    border: "none",
                                    padding: "3px",
                                    backgroundColor: rule.is_active
                                      ? "#2f6b1f"
                                      : "#ef3d32",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: rule.is_active
                                      ? "flex-end"
                                      : "flex-start",
                                    cursor: updatingRuleIds.includes(rule.id)
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: updatingRuleIds.includes(rule.id)
                                      ? 0.7
                                      : 1,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                      backgroundColor: "#ffffff",
                                      display: "block",
                                      boxShadow:
                                        "0 1px 3px rgba(0, 0, 0, 0.2)",
                                    }}
                                  />
                                </button>
                                <span
                                  className="small font-w500"
                                  style={{
                                    minWidth: "60px",
                                    textAlign: "left",
                                    whiteSpace: "nowrap",
                                    color: rule.is_active ? "#2f6b1f" : "#ef3d32",
                                  }}
                                >
                                  {rule.is_active ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center py-4 text-muted">
                            No rules available for this promo.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInfoModal && selectedInfoPromo && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            style={{ maxWidth: "750px" }}
          >
            <div className="modal-content">
              <div className="modal-header align-items-center">
                <h5 className="modal-title m-0">
                  Promo Details: <span className="text-primary font-w600">{selectedInfoPromo.code}</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleInfoClose}
                />
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Code</small>
                      <span className="font-w600 text-dark fs-15">{selectedInfoPromo.code}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Name</small>
                      <span className="font-w600 text-dark fs-15">{selectedInfoPromo.name || "-"}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Discount</small>
                      <span className="font-w600 text-dark">
                        {selectedInfoPromo.discount_value}{" "}
                        {selectedInfoPromo.discount_type === "PERCENTAGE" ? "%" : "USD"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Discount Type</small>
                      <span className="font-w600 text-dark">{selectedInfoPromo.discount_type}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Status</small>
                      <span
                        className={`badge ${
                          selectedInfoPromo.is_active ? "badge-success" : "badge-danger"
                        }`}
                      >
                        {selectedInfoPromo.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Start At</small>
                      <span className="font-w500">{formatDisplayDate(selectedInfoPromo.start_at)}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Expires At</small>
                      <span className="font-w500">{formatDisplayDate(selectedInfoPromo.expires_at)}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Max Redemptions</small>
                      <span className="font-w500">{selectedInfoPromo.max_redemptions ?? "-"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Max Redemptions Per User</small>
                      <span className="font-w500">{selectedInfoPromo.max_redemptions_per_user ?? "-"}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Created On</small>
                      <span className="font-w500">{formatDisplayDate(selectedInfoPromo.created_at)}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="p-3 bg-light rounded">
                      <small className="text-muted d-block mb-1">Updated On</small>
                      <span className="font-w500">{formatDisplayDate(selectedInfoPromo.updated_at)}</span>
                    </div>
                  </div>
                  {selectedInfoPromo.description && (
                    <div className="col-12">
                      <div className="p-3 bg-light rounded">
                        <small className="text-muted d-block mb-1">Description</small>
                        <span className="font-w500">{selectedInfoPromo.description}</span>
                      </div>
                    </div>
                  )}
                  <div className="col-12 mt-3">
                    <label className="form-label font-w600 mb-2">Rules</label>
                    <div className="table-responsive border rounded">
                      <table className="table table-sm align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Rule ID</th>
                            <th>Service Type</th>
                            <th>Billed Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInfoPromo.rules?.length ? (
                            selectedInfoPromo.rules.map((rule) => (
                              <tr key={rule.id}>
                                <td>{rule.id}</td>
                                <td className="font-w600">{rule.service_type}</td>
                                <td className="text-capitalize">{rule.billed_type}</td>
                                <td>
                                  <span
                                    className={`badge badge-xs ${
                                      rule.is_active ? "badge-success" : "badge-danger"
                                    }`}
                                  >
                                    {rule.is_active ? "Active" : "Inactive"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center py-3 text-muted">
                                No rules attached.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleInfoClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <SendPromoModal
        show={showSendModal}
        promo={selectedSendPromo}
        onClose={handleSendClose}
      />
    </Fragment>
  );
};

export default PromoCodesList;
